import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Card } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { SITE_STATUS_BADGE_CLASS, SITE_STATUS_LABELS, type SiteStatus } from '../lib/sites';
import SiteDetailModal from '../components/SiteDetailModal';
import { siteIndustryLabel } from '../lib/siteIndustry';
import type { AdminSiteIndustry } from '../lib/api';

interface Site {
  id: string;
  name: string;
  address: string | null;
  addressDetail: string | null;
  industryType: string | null;
  industrySubType: string | null;
  industryId: string | null;
  industry: AdminSiteIndustry | null;
  status: SiteStatus;
  createdAt: string;
  organization: {
    id: string;
    name: string;
    category: string;
  };
  _count: {
    inspections: number;
    assignments: number;
  };
}

export default function Sites() {
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'sites', { search }],
    queryFn: () => adminApi.getSites({ limit: 100, search: search || undefined }),
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const sites = data.data || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">현장 관리</h2>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            총 {data.meta?.total || sites.length}개
          </div>
        </div>
      </div>

      {/* 검색 */}
      <form onSubmit={handleSearch} className="mb-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="현장명 또는 주소로 검색..."
            className="flex-1 max-w-md px-4 py-2 border border-gray-300 rounded-control focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
          <Button type="submit" variant="primary">
            검색
          </Button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSearchInput('');
              }}
              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-control hover:bg-gray-200"
            >
              초기화
            </button>
          )}
        </div>
      </form>

      <Card padding="none" radius="lg" className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                현장명
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                소속 조직
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                주소
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                업종
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                담당자
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                점검
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                상태
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sites.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  {search ? '검색 결과가 없습니다.' : '등록된 현장이 없습니다.'}
                </td>
              </tr>
            ) : (
              sites.map((site) => (
                <SiteRow key={site.id} site={site} onClick={() => setSelectedSite(site)} />
              ))
            )}
          </tbody>
        </table>
      </Card>

      {/* 현장 상세 모달 */}
      <SiteDetailModal
        site={selectedSite}
        isOpen={!!selectedSite}
        onClose={() => setSelectedSite(null)}
      />
    </div>
  );
}

function SiteRow({ site, onClick }: { site: Site; onClick: () => void }) {
  const fullAddress = [site.address, site.addressDetail].filter(Boolean).join(' ');

  return (
    <tr className="hover:bg-gray-50 cursor-pointer" onClick={onClick}>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="font-medium text-gray-900">{site.name}</div>
        <div className="text-xs text-gray-400 font-mono">{site.id.slice(0, 8)}...</div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm text-gray-900">{site.organization?.name ?? '-'}</div>
      </td>
      <td className="px-6 py-4">
        <div className="text-sm text-gray-500 max-w-xs truncate" title={fullAddress || '-'}>
          {fullAddress || '-'}
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {siteIndustryLabel(site) ?? '-'}
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {site._count.assignments}명
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        {site._count.inspections}건
      </td>
      <td className="px-6 py-4 whitespace-nowrap">
        <span
          className={`px-2 py-1 text-xs font-medium rounded-full ${SITE_STATUS_BADGE_CLASS[site.status]}`}
        >
          {SITE_STATUS_LABELS[site.status]}
        </span>
      </td>
    </tr>
  );
}
